import { useMemo } from 'react';
import { AuthenticationStatus } from '../../../auth/types';
import { isServerSide } from '../../rendering';
import { CustomerInfoSchema } from '../../types';
import { transformAuth0IntoQwaSession } from '../../../auth/session';
import { useUser } from '@auth0/nextjs-auth0/client';
import { SUBS_SCHEMA } from '../../../config/auth';
import { CONTENT_AI_USER_TYPE, SUBSCRIPTION_STATUS } from '../../../constants/common';

export const useSession = () => {
  const user = useUser();
  let qwaSession;
  if (user?.user && !user.isLoading) {
    qwaSession = user?.user;
  }

  const session = useMemo(() => {
    return {
      session: qwaSession ? transformAuth0IntoQwaSession(qwaSession) : undefined,
      authState: qwaSession ? AuthenticationStatus.Authenticated : AuthenticationStatus.Loading,
    };
  }, [qwaSession]); // Update only when qwaSession changes

  return session;
};

/**
 * Whether or not the current user is a "Test Drive" user
 * @returns `undefined` if still loading, otherwise returns a boolean
 */
export const useIsTestDriveUser = () => {
  const { session } = useSession();
  const testDriveDomain = useTestDriveDomain();

  const qwaSession = session;
  if (!qwaSession) {
    return undefined;
  }

  const qwaUser = qwaSession.user;
  if (!qwaUser) {
    return undefined;
  }

  const isOnlyAuthorizedForSubs =
    qwaUser.groups && Array.isArray(qwaUser.groups) && qwaUser.groups.length === 1 && qwaUser.groups[0] === SUBS_SCHEMA;

  return Boolean(isOnlyAuthorizedForSubs || testDriveDomain !== undefined);
};

export const useIsSubscriberUser = () => {
  const { session } = useSession();
  if (!session) {
    return undefined;
  }

  if (isServerSide()) {
    return undefined;
  }
  const userInfo = session.user;
  if (!userInfo) {
    return undefined;
  }

  const isSubscriberUser = session.selected_schema === SUBS_SCHEMA;
  return isSubscriberUser;
};

export const useIsContentAIUser = () => {
  const { session } = useSession();
  if (!session) {
    return undefined;
  }

  if (isServerSide()) {
    return undefined;
  }
  const userInfo = session.user;
  if (!userInfo) {
    return undefined;
  }
  const IsContentAIUser = userInfo && userInfo.userType === CONTENT_AI_USER_TYPE;
  return IsContentAIUser;
};

export const useIsInternalUser = () => {
  const { session } = useSession();
  const isTestDriveUser = useIsTestDriveUser();

  const qwaSession = session;
  if (!qwaSession) {
    return undefined;
  }

  const qwaUser = qwaSession.user;
  if (!qwaUser) {
    return undefined;
  }

  if (isTestDriveUser) {
    return false;
  }

  return Boolean(qwaUser.email && qwaUser.email.endsWith('@quattr.com'));
};

// export const useIsFreeTrialUser = () => {
//   const isContentAIUser = useIsContentAIUser();
//   const isInternalUser = useIsInternalUser();

//   if (isContentAIUser === undefined || isInternalUser === undefined) {
//     return undefined;
//   }

//   return isContentAIUser && !isInternalUser;
// };

export const useIsFreeTrialContentAIUser = () => {
  const isContentAIUser = useIsContentAIUser();
  const isInternalUser = useIsInternalUser();

  const { session } = useSession();
  if (!session) {
    return undefined;
  }
  const subStatus = session?.user?.subscription_status;
  const isSubscriptionNotActivated = ![
    SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
    SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
    SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
  ].includes(subStatus?.toLowerCase() || '');

  if (isContentAIUser === undefined || isInternalUser === undefined) {
    return undefined;
  }
  return isSubscriptionNotActivated && isContentAIUser && !isInternalUser;
};

export const useIsProspectUser = () => {
  const isSubscriber = useIsSubscriberUser();
  const isInternalUser = useIsInternalUser();

  if (isSubscriber === undefined || isInternalUser === undefined) {
    return undefined;
  }

  return isSubscriber && !isInternalUser;
};

export const useIsCustomer = () => {
  const isContentAIUser = useIsContentAIUser();
  const isInternalUser = useIsInternalUser();
  const isSubscriber = useIsSubscriberUser();

  if (isContentAIUser === undefined || isInternalUser === undefined || isSubscriber === undefined) {
    return undefined;
  }

  return !isContentAIUser && !isSubscriber && !isInternalUser;
};

export const useIsEnterpriseUser = () => {
  return useIsCustomer();
};

/**
 * Returns the current domain from the JWT/Session for a test drive user
 * @returns `undefined` if still no domain is set or user is not a test drive user.
 */
export const useTestDriveDomain = () => {
  // const { session } = useSession();
  const subscriberInfo = getSubscriberUserInfo();
  if (!subscriberInfo) {
    return undefined;
  }

  return subscriberInfo.domain;
};

/**
 *
 * @returns `undefined` if loading is still in progress or there is no valid session
 */
export const useSchema = (): string | undefined => {
  const { session } = useSession();
  if (!session || !session.user) {
    return undefined;
  }

  // if on the server, don't try to access local storage
  if (typeof localStorage === 'undefined') {
    return undefined;
  }

  const userSelectedSchema = session.selected_schema;
  return userSelectedSchema;
};

export const useCustomerInfo = (customerInfo = [] as CustomerInfoSchema[]) => {
  const schemaName = useSchema();
  const customerData = customerInfo.find((o) => o?.cust_code === schemaName);
  return customerData;
};

export const getSubscriberUserInfo = ():
  | {
      email: string;
      domain: string;
      id: string;
      full_name: string;
      domain_token_id: string;
      iat: number;
      exp: number;
      iss: string;
    }
  | undefined => {
  const subscriberInfo = isServerSide() ? '' : localStorage.getItem('sub-userInfo');
  return subscriberInfo ? JSON.parse(subscriberInfo) : undefined;
};
